import { Box, Stack, Typography } from "@mui/material";
import React from "react";

interface IPageHeaderProps {
    /**
     * Main title of the page
     */
    title: string;
    /**
     * Optional subtitle below the title
     */
    subhead?: React.ReactNode;
    /**
     * Left side content (badges, chips, metadata)
     */
    leftContent?: React.ReactNode;
    /**
     * Right side actions (buttons, dropdowns) - appears in bottom row
     */
    actions?: React.ReactNode;
    /**
     * Actions that appear inline with the title
     */
    titleActions?: React.ReactNode;
    /**
     * Use smaller title font size
     */
    compact?: boolean;
}

/**
 * Reusable page header component for consistent page layouts
 * Includes  title, and flexible content areas
 */
export const PageHeader = ({
    title,
    subhead,
    leftContent,
    actions,
    titleActions,
    compact = false,
}: IPageHeaderProps) => {
    return (
        <Box
            sx={{
                borderBottom: "1px solid",
                borderColor: "divider",
                backgroundColor: "#f8fafc",
                px: { xs: 2, sm: 3 },
                py: 1.5,
            }}
        >
            {/* Title Row */}
            <Box
                sx={{
                    display: "flex",
                    flexWrap: "nowrap",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 2,
                    mt: 1,
                    mb: 1.5,
                }}
            >
                <Typography
                    variant="h4"
                    component="h1"
                    sx={{
                        fontWeight: 700,
                        color: "#233876",
                        fontSize: compact ? "1.25rem" : "1.5rem",
                        flex: "1 1 auto",
                        minWidth: 0,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                    }}
                >
                    {title}
                </Typography>
                {titleActions && (
                    <Stack
                        direction="row"
                        spacing={0.5}
                        alignItems="center"
                        sx={{ flex: "0 0 auto", flexShrink: 0 }}
                    >
                        {titleActions}
                    </Stack>
                )}
            </Box>

            {/* Subhead */}
            {subhead && (
                <Box sx={{ flex: "1 1 auto", minWidth: 0, mb: 1.5 }}>
                    {subhead}
                </Box>
            )}

            {/* Main content row */}
            <Box
                sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "space-between",
                    alignItems: "flex-end",
                    gap: 2,
                }}
            >
                {/* Left content area */}
                <Box sx={{ flex: "1 1 auto", minWidth: 0 }}>{leftContent}</Box>

                {/* Actions */}
                {actions && (
                    <Box sx={{ flex: "0 0 auto" }}>
                        <Stack
                            direction="row"
                            spacing={0.5}
                            alignItems="center"
                        >
                            {actions}
                        </Stack>
                    </Box>
                )}
            </Box>
        </Box>
    );
};
